import type { AgendaDia, CitaVista, ConfigItem, ServicioVista } from '@barbones/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/cliente';

export interface Barbero {
  id: string;
  nombre: string;
  /** Solo al pedirlos por día: su horario de ese día (hora local `HH:mm`). */
  horario?: { entrada: string; salida: string; comidaInicio: string | null; comidaFin: string | null };
}

export const useAgendaDia = (dia: string) =>
  useQuery({ queryKey: ['agenda', 'dia', dia], queryFn: () => api<AgendaDia>('/agenda/dia', { query: { dia } }) });

export const useServicios = (soloActivos = true) =>
  useQuery({
    queryKey: ['servicios', soloActivos],
    queryFn: () => api<ServicioVista[]>('/servicios', { query: { activos: soloActivos ? 'true' : undefined } }),
    staleTime: 60_000,
  });

/**
 * Sin `dia`: todo el personal que atiende (reportes, servicios, caja).
 * Con `dia`: solo quien puede recibir citas y trabaja ese día (agendar, reprogramar, asignar).
 */
export const useBarberos = (dia?: string) =>
  useQuery({
    queryKey: ['barberos', dia ?? 'todos'],
    queryFn: () => api<Barbero[]>('/personal/barberos', { query: { dia } }),
    staleTime: 60_000,
  });

export const usePendientes = (desde: string, hasta: string, habilitado = true) =>
  useQuery({
    queryKey: ['pendientes', desde, hasta],
    queryFn: () => api<CitaVista[]>('/agenda/pendientes-asignar', { query: { desde, hasta } }),
    enabled: habilitado,
  });

/** Valor de una configuración (con su valor por defecto mientras carga). */
export function useConfig<T>(clave: string, porDefecto: T): T {
  const consulta = useQuery({ queryKey: ['configuracion'], queryFn: () => api<ConfigItem[]>('/configuracion'), staleTime: 60_000, retry: false });
  const item = consulta.data?.find((c) => c.clave === clave);
  return (item?.valor as T | undefined) ?? porDefecto;
}
